import EditIcon from "@mui/icons-material/Edit";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

const columns = [
  { key: "supplier", label: "Proveedor", width: "minmax(200px, 1.65fr)" },
  { key: "contact", label: "Contacto", width: "minmax(170px, 1.3fr)" },
  { key: "categories", label: "Categorías", width: "minmax(260px, 2fr)" },
  { key: "actions", label: "Acciones", width: "minmax(125px, 0.8fr)" },
];

const tableGridTemplate = columns.map((column) => column.width).join(" ");

const gridSx = {
  display: "grid",
  gridTemplateColumns: tableGridTemplate,
  columnGap: 1,
  alignItems: "center",
  "& > *": { minWidth: 0 },
};

function TextCell({ children, muted = false }) {
  return (
    <Typography
      noWrap
      title={typeof children === "string" ? children : undefined}
      sx={{
        color: muted ? "neutral.600" : "primary.900",
        fontSize: 14,
        fontWeight: muted ? 600 : 700,
        fontStyle: muted ? "italic" : "normal",
        textAlign: "center",
      }}
    >
      {children}
    </Typography>
  );
}

function CategoryPill({ category }) {
  return (
    <Box
      sx={{
        minWidth: 92,
        maxWidth: 190,
        px: 1.4,
        py: 0.55,
        borderRadius: "16px",
        bgcolor: "secondary.50",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.28)",
      }}
    >
      <Typography
        noWrap
        title={category.name}
        sx={{
          color: "primary.900",
          fontSize: 12,
          fontWeight: 700,
          textAlign: "center",
        }}
      >
        {category.name}
      </Typography>
    </Box>
  );
}

export default function SuppliersTable({
  rows = [],
  loading = false,
  expandedSupplierIds,
  onEdit,
  onToggleExpanded,
  emptyMessage = "Todavía no hay proveedores cargados.",
}) {
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        borderRadius: "20px",
        overflow: "hidden",
      }}
    >
      <Box sx={{ height: "100%", overflowX: "auto", overflowY: "hidden" }}>
        <Box
          sx={{
            minWidth: 760,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              ...gridSx,
              bgcolor: "neutral.600",
              flexShrink: 0,
              px: 2,
              py: 1.6,
              overflowY: "hidden",
              scrollbarGutter: "stable",
            }}
          >
            {columns.map((column) => (
              <Typography
                key={column.key}
                sx={{
                  color: "logoblanco",
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: 1.15,
                  textAlign: "center",
                }}
              >
                {column.label}
              </Typography>
            ))}
          </Box>

          <Box
            sx={{
              bgcolor: "neutral.50",
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              scrollbarGutter: "stable",
            }}
          >
            {rows.map((supplier) => {
              const expanded = expandedSupplierIds?.has(supplier.id);

              return (
                <Box
                  key={supplier.id}
                  sx={{
                    borderBottom: "1px solid rgba(0,0,0,.06)",
                    bgcolor: expanded ? "secondary.50" : "transparent",
                    transition: "background-color 160ms ease",
                    "&:hover": {
                      bgcolor: expanded ? "secondary.50" : "rgba(0,0,0,.03)",
                    },
                  }}
                >
                  <Box sx={{ ...gridSx, minHeight: 58, px: 2, py: 1.1 }}>
                    <Box>
                      <TextCell>{supplier.name}</TextCell>
                    </Box>
                    <Box>
                      <TextCell muted={!supplier.phone}>
                        {supplier.phone || "Sin contacto"}
                      </TextCell>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <Box
                        sx={{
                          minWidth: 38,
                          px: 1.1,
                          py: 0.45,
                          borderRadius: "16px",
                          bgcolor: "secondary.100",
                          boxShadow:
                            "inset 0px 0px 3px rgba(142, 142, 159, 0.28)",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "primary.900",
                            fontSize: 13,
                            fontWeight: 800,
                            textAlign: "center",
                          }}
                        >
                          {supplier.categoryCount}
                        </Typography>
                      </Box>
                    </Box>
                    <Stack
                      direction="row"
                      spacing={0.9}
                      sx={{
                        width: "100%",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Tooltip title="Editar proveedor" arrow>
                        <IconButton
                          size="small"
                          disabled={loading}
                          onClick={(event) => {
                            event.stopPropagation();
                            onEdit?.(supplier);
                          }}
                          aria-label={`Editar ${supplier.name}`}
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: "neutral.50",
                            boxShadow: "0px 2px 8px rgba(0,0,0,.08)",
                            "&:hover": { bgcolor: "secondary.100" },
                          }}
                        >
                          <EditIcon
                            sx={{ fontSize: 18, color: "primary.900" }}
                          />
                        </IconButton>
                      </Tooltip>

                      <Tooltip
                        title={
                          expanded ? "Ocultar categorías" : "Ver categorías"
                        }
                        arrow
                      >
                        <IconButton
                          size="small"
                          disabled={loading}
                          onClick={() => onToggleExpanded?.(supplier)}
                          aria-label={`${expanded ? "Ocultar" : "Ver"} categorías de ${
                            supplier.name
                          }`}
                          aria-expanded={Boolean(expanded)}
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: "secondary.500",
                            boxShadow: "0px 2px 8px rgba(0,0,0,.08)",
                            "&:hover": { bgcolor: "secondary.600" },
                            "&.Mui-disabled": {
                              bgcolor: "secondary.300",
                            },
                          }}
                        >
                          {expanded ? (
                            <ExpandLessIcon
                              sx={{ fontSize: 22, color: "logoblanco" }}
                            />
                          ) : (
                            <ExpandMoreIcon
                              sx={{ fontSize: 22, color: "logoblanco" }}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>

                  {expanded && (
                    <Box
                      sx={{
                        ...gridSx,
                        px: 2,
                        pb: 1.4,
                        animation: "supplierCategoriesIn 160ms ease-out",
                        "@keyframes supplierCategoriesIn": {
                          from: { opacity: 0, transform: "translateY(-4px)" },
                          to: { opacity: 1, transform: "translateY(0)" },
                        },
                      }}
                    >
                      <Box sx={{ gridColumn: "1 / 3" }} />
                      <Box
                        sx={{
                          gridColumn: "3 / 4",
                          display: "flex",
                          minWidth: 0,
                          justifyContent: "center",
                        }}
                      >
                        {supplier.categories.length ? (
                          <Stack
                            direction="row"
                            spacing={0.8}
                            useFlexGap
                            sx={{
                              flexWrap: "wrap",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {supplier.categories.map((category) => (
                              <CategoryPill
                                key={category.id}
                                category={category}
                              />
                            ))}
                          </Stack>
                        ) : (
                          <Box
                            sx={{
                              px: 1.4,
                              py: 0.8,
                              borderRadius: "16px",
                              bgcolor: "neutral.100",
                            }}
                          >
                            <Typography
                              sx={{
                                color: "neutral.600",
                                fontSize: 12,
                                fontWeight: 700,
                                fontStyle: "italic",
                                textAlign: "center",
                              }}
                            >
                              Sin categorías asociadas
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })}

            {rows.length === 0 && (
              <Box sx={{ p: 3 }}>
                <Typography sx={{ color: "neutral.700", fontWeight: 600 }}>
                  {loading ? "Cargando proveedores..." : emptyMessage}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
